import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Cpu, ArrowLeft, Loader2, DollarSign, Activity, AlertTriangle, Target, TrendingUp, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RANGES = [7, 30, 90];

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const FEATURE_LABELS = {
  coach: "AI Coach",
  affirmation: "Daily affirmations",
  journal_summary: "Journal summaries",
  assessment: "Check-in narratives",
  course_generation: "Course generation",
  transcribe: "Voice transcription",
};
const label = (f) => FEATURE_LABELS[f] || f;

export default function AdminAIUsage() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/ai-usage?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const saveBudget = async () => {
    const v = Number(budgetInput);
    if (isNaN(v) || v < 0) { toast.error("Enter a valid amount."); return; }
    setSavingBudget(true);
    try {
      await api.put("/admin/ai-budget", { monthly_usd: v });
      toast.success("Monthly budget updated.");
      setEditingBudget(false);
      load();
    } catch {
      toast.error("Couldn't save the budget.");
    } finally {
      setSavingBudget(false);
    }
  };

  const saveLimit = async () => {
    const v = Math.floor(Number(limitInput));
    if (isNaN(v) || v < 0) { toast.error("Enter a whole number (0 = unlimited)."); return; }
    setSavingLimit(true);
    try {
      await api.put("/admin/coach-limit", { value: v });
      toast.success("Coach message limit updated.");
      setEditingLimit(false);
      load();
    } catch {
      toast.error("Couldn't save the limit.");
    } finally {
      setSavingLimit(false);
    }
  };

  if (user && user.role !== "owner") return <Navigate to="/home" replace />;

  const maxFeatureCost = Math.max(1e-9, ...((data?.by_feature || []).map((f) => f.cost)));
  const overThreshold = data && data.threshold_usd > 0 && data.today_cost >= data.threshold_usd;
  const budget = Number(data?.budget_usd) || 0;
  const monthCost = Number(data?.month_cost) || 0;
  const budgetPct = budget > 0 ? Math.min(100, (monthCost / budget) * 100) : 0;
  const budgetColor = budgetPct >= 100 ? "#C0453B" : budgetPct >= 75 ? "#D08A2E" : "#C89B55";
  const maxWeekCost = Math.max(1e-9, ...((data?.by_week || []).map((w) => w.cost)));

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="admin-ai-usage-page">
      <Link to="/settings" className="inline-flex items-center gap-2 text-sm text-[#59535B] hover:text-[#291B2D] transition-smooth" data-testid="admin-ai-usage-back">
        <ArrowLeft className="h-4 w-4" /> Back to settings
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Cpu className="text-[#C89B55]" size={22} />
          <div>
            <h1 className="text-2xl font-serif text-[#291B2D]">AI Usage</h1>
            <p className="text-sm text-[#59535B]">Estimated Emergent LLM spend across your AI features.</p>
          </div>
        </div>
        <div className="flex gap-1 bg-[#F0E9DE] rounded-full p-1" data-testid="ai-usage-range">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              data-testid={`ai-usage-range-${r}`}
              className={`px-4 py-1.5 rounded-full text-sm transition-smooth ${days === r ? "bg-[#291B2D] text-[#FCFAF6]" : "text-[#59535B]"}`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#8A838E]" data-testid="ai-usage-loading">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : !data ? (
        <p className="text-[#8A838E]">Couldn't load usage right now.</p>
      ) : (
        <>
          <p className="text-xs text-[#8A838E] -mt-4">
            Estimated from token usage &amp; public model pricing — a close proxy, not your exact Emergent invoice.
            For your real balance &amp; auto top-up, see Profile → Manage plan → Universal Key.
          </p>

          {overThreshold && (
            <div className="flex items-start gap-3 rounded-2xl border border-[#E5B44A] bg-[#FBF3E1] p-4" data-testid="ai-usage-threshold-banner">
              <AlertTriangle className="h-5 w-5 text-[#B9822B] shrink-0 mt-0.5" />
              <p className="text-sm text-[#7A5A1E]">
                Today's estimated spend ({money(data.today_cost)}) is over your {money(data.threshold_usd)} daily alert threshold.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard testid="ai-usage-total-cost" icon={<DollarSign className="h-4 w-4" />} label={`Est. cost · ${days}d`} value={money(data.total_cost)} />
            <StatCard testid="ai-usage-today-cost" icon={<DollarSign className="h-4 w-4" />} label="Est. cost · today" value={money(data.today_cost)} />
            <StatCard testid="ai-usage-total-calls" icon={<Activity className="h-4 w-4" />} label={`AI calls · ${days}d`} value={data.total_calls.toLocaleString()} />
          </div>

          <section className="bg-[#FCFAF6] rounded-2xl border border-[#E5DED4] p-5" data-testid="ai-usage-budget">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#291B2D] flex items-center gap-2"><Target className="h-4 w-4 text-[#C89B55]" /> Monthly AI budget</h2>
              {!editingBudget && (
                <button
                  data-testid="ai-budget-edit"
                  onClick={() => { setBudgetInput(budget ? String(budget) : ""); setEditingBudget(true); }}
                  className="text-sm text-[#C89B55] hover:text-[#291B2D] transition-smooth"
                >
                  {budget > 0 ? "Edit" : "Set budget"}
                </button>
              )}
            </div>

            {editingBudget ? (
              <div className="flex items-center gap-2" data-testid="ai-budget-editor">
                <span className="text-[#59535B]">$</span>
                <input
                  type="number" min="0" step="1" value={budgetInput}
                  onChange={(e) => setBudgetInput(e.target.value)}
                  data-testid="ai-budget-input"
                  placeholder="e.g. 100"
                  className="w-32 px-3 py-2 rounded-lg border border-[#E5DED4] bg-white text-[#291B2D] focus:outline-none focus:border-[#C89B55]"
                />
                <span className="text-sm text-[#8A838E]">/ month</span>
                <button onClick={saveBudget} disabled={savingBudget} data-testid="ai-budget-save"
                  className="ml-auto px-4 py-2 rounded-full bg-[#291B2D] text-[#FCFAF6] text-sm disabled:opacity-60">
                  {savingBudget ? "Saving…" : "Save"}
                </button>
                <button onClick={() => setEditingBudget(false)} className="px-3 py-2 text-sm text-[#8A838E]">Cancel</button>
              </div>
            ) : budget > 0 ? (
              <div data-testid="ai-budget-progress">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-[#291B2D]">{money(monthCost)} <span className="text-[#8A838E]">of {money(budget)} this month</span></span>
                  <span className="font-medium" style={{ color: budgetColor }}>{Math.round(budgetPct)}%</span>
                </div>
                <div className="h-3 rounded-full bg-[#EFE7DB] overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${Math.max(2, budgetPct)}%`, background: budgetColor }} />
                </div>
                {budgetPct >= 100 && (
                  <p className="text-xs text-[#C0453B] mt-2">You've reached your monthly AI budget.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-[#8A838E]">No budget set. Set one to track spend against a monthly target.</p>
            )}
          </section>

          <section className="bg-[#FCFAF6] rounded-2xl border border-[#E5DED4] p-5" data-testid="ai-usage-coach-limit">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[#291B2D] flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#C89B55]" /> Daily AI Coach limit / member</h2>
              {!editingLimit && (
                <button
                  data-testid="coach-limit-edit"
                  onClick={() => { setLimitInput(String(data.coach_daily_limit ?? 25)); setEditingLimit(true); }}
                  className="text-sm text-[#C89B55] hover:text-[#291B2D] transition-smooth"
                >
                  Edit
                </button>
              )}
            </div>
            {editingLimit ? (
              <div className="flex items-center gap-2 mt-3" data-testid="coach-limit-editor">
                <input
                  type="number" min="0" step="1" value={limitInput}
                  onChange={(e) => setLimitInput(e.target.value)}
                  data-testid="coach-limit-input"
                  className="w-28 px-3 py-2 rounded-lg border border-[#E5DED4] bg-white text-[#291B2D] focus:outline-none focus:border-[#C89B55]"
                />
                <span className="text-sm text-[#8A838E]">messages / day (0 = unlimited)</span>
                <button onClick={saveLimit} disabled={savingLimit} data-testid="coach-limit-save"
                  className="ml-auto px-4 py-2 rounded-full bg-[#291B2D] text-[#FCFAF6] text-sm disabled:opacity-60">
                  {savingLimit ? "Saving…" : "Save"}
                </button>
                <button onClick={() => setEditingLimit(false)} className="px-3 py-2 text-sm text-[#8A838E]">Cancel</button>
              </div>
            ) : (
              <p className="text-sm text-[#59535B] mt-2" data-testid="coach-limit-value">
                {(data.coach_daily_limit ?? 25) === 0
                  ? "Unlimited — members can send as many coach messages as they like."
                  : `Each member can send up to ${data.coach_daily_limit} coach messages per day. Admins are exempt.`}
              </p>
            )}
          </section>

          <section data-testid="ai-usage-by-feature">
            <h2 className="text-sm font-semibold text-[#291B2D] mb-3">By feature</h2>
            <div className="space-y-3 bg-[#FCFAF6] rounded-2xl border border-[#E5DED4] p-5">
              {data.by_feature.length === 0 ? (
                <p className="text-sm text-[#8A838E]">No AI usage in this range yet.</p>
              ) : data.by_feature.map((f) => (
                <div key={f.feature} data-testid={`ai-feature-${f.feature}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-[#291B2D]">{label(f.feature)} <span className="text-[#8A838E]">· {f.calls}</span></span>
                    <span className="text-[#291B2D] font-medium">{money(f.cost)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#EFE7DB] overflow-hidden">
                    <div className="h-full bg-[#C89B55]" style={{ width: `${Math.max(4, (f.cost / maxFeatureCost) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section data-testid="ai-usage-weekly">
            <h2 className="text-sm font-semibold text-[#291B2D] mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#C89B55]" /> Weekly spend (last 8 weeks)</h2>
            <div className="bg-[#FCFAF6] rounded-2xl border border-[#E5DED4] p-5">
              {(!data.by_week || data.by_week.length === 0) ? (
                <p className="text-sm text-[#8A838E]">No data yet.</p>
              ) : (
                <div className="flex items-end justify-between gap-2 h-40" data-testid="ai-weekly-bars">
                  {data.by_week.map((w, i) => {
                    const isLast = i === data.by_week.length - 1;
                    return (
                      <div key={w.week_start} className="flex-1 flex flex-col items-center justify-end h-full" title={`Week of ${w.label}: ${money(w.cost)} · ${w.calls} calls`}>
                        <span className="text-[10px] text-[#59535B] mb-1">{w.cost >= 0.005 ? money(w.cost) : ""}</span>
                        <div className="w-full rounded-t-md transition-all" style={{ height: `${Math.max(3, (w.cost / maxWeekCost) * 100)}%`, background: isLast ? "#291B2D" : "#C89B55" }} />
                        <span className="text-[10px] text-[#8A838E] mt-2 whitespace-nowrap">{w.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section data-testid="ai-usage-by-day">
              <h2 className="text-sm font-semibold text-[#291B2D] mb-3">Daily spend</h2>
              <div className="bg-[#FCFAF6] rounded-2xl border border-[#E5DED4] p-2 max-h-72 overflow-auto">
                {data.by_day.length === 0 ? (
                  <p className="text-sm text-[#8A838E] p-3">No data.</p>
                ) : [...data.by_day].reverse().map((d) => (
                  <div key={d.date} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-[#59535B]">{d.date}</span>
                    <span className="text-[#291B2D]">{money(d.cost)} <span className="text-[#8A838E]">· {d.calls}</span></span>
                  </div>
                ))}
              </div>
            </section>

            <section data-testid="ai-usage-top-users">
              <h2 className="text-sm font-semibold text-[#291B2D] mb-3">Top members</h2>
              <div className="bg-[#FCFAF6] rounded-2xl border border-[#E5DED4] p-2 max-h-72 overflow-auto">
                {data.top_users.length === 0 ? (
                  <p className="text-sm text-[#8A838E] p-3">No member usage yet.</p>
                ) : data.top_users.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-[#291B2D] truncate max-w-[60%]">{u.name}<span className="block text-xs text-[#8A838E] truncate">{u.email}</span></span>
                    <span className="text-[#291B2D] shrink-0">{money(u.cost)} <span className="text-[#8A838E]">· {u.calls}</span></span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="bg-[#291B2D] rounded-2xl p-5 text-[#FCFAF6]" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#C89B55] text-xs uppercase tracking-wide">{icon}{label}</div>
      <div className="text-3xl font-light mt-2">{value}</div>
    </div>
  );
}
